import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration } from '../../../data/configuration.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface CarpetViewProps {
	interiorConfigurator: InteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface CarpetViewContextType {
	selectedCarpetItemId: string;
	handleCarpetItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function CarpetView(props: CarpetViewProps) {

	// initial values.
	const initialCarpetItemId = Configuration.carpetColour;
	const initialCarpetItemName = () => getItemName(initialCarpetItemId);

	// states.
	const [selectedCarpetItemId, setSelectedCarpetItemId] = useState(initialCarpetItemId);
	const [selectedCarpetItemName, setSelectedCarpetItemName] = useState(initialCarpetItemName);

	// handlers.
	const handleCarpetItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedCarpetItemId(id);
		setSelectedCarpetItemName(getItemName(id));

		// update the configuration.
		Configuration.carpetColour = id;
		Configuration.updateCosts();

		// select the carpet colour on the car.
		props.interiorConfigurator.selectCarpetColour(id);
	};

	// return the HTML.
	return (
		<CarpetViewContext.Provider value={{ selectedCarpetItemId, handleCarpetItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-carpet">CARPET</h2>
					</div>
					
					<SectionPanel
						subHeading={'CARPET'}
						modalContent={CloudData.getModalContent(selectedCarpetItemId, CloudData.carpetColourOptions)}
					>
						<SelectedSectionItem
							itemName={selectedCarpetItemName}
							sectionPrice={CloudData.getPrice(selectedCarpetItemId, CloudData.carpetColourOptions, { ref: false })}
						/>
						<CarpetItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</CarpetViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const CarpetViewContext = createContext<CarpetViewContextType | undefined>(undefined);

function CarpetItems() {
	return (
		<ViewContentRows maxRowLength={5}>
			<CarpetItem id={'CSW001'} imageFilePath={'black-standard-weave'} />
			<CarpetItem id={'CBW004'} imageFilePath={'black-box-weave'} />
			<CarpetItem id={'CBW003'} imageFilePath={'biscuit-box-weave'} />
			<CarpetItem id={'CBW005'} imageFilePath={'blue-box-weave'} />
			<CarpetItem id={'CBW006'} imageFilePath={'brown-box-weave'} />
			<CarpetItem id={'CBW001'} imageFilePath={'anthracite-box-weave'} />
			<CarpetItem id={'CBW007'} imageFilePath={'green-box-weave'} />
			<CarpetItem id={'CBW002'} imageFilePath={'oatmeal-box-weave'} />
			<CarpetItem id={'CBW008'} imageFilePath={'red-box-weave'} />
		</ViewContentRows>
	);
}

function CarpetItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(CarpetViewContext);
	if (!context) {
		throw new Error('CarpetViewContext must be used within a CarpetView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/carpet/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedCarpetItemId}
			onItemClicked={context.handleCarpetItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.carpetColourOptions);
}
